import { CookieOptions, SimpleStorage } from './storage.types';
import JsCookie from 'js-cookie';
import { AuthSdkError } from '../lib/auth-sdk-error';

export interface ICookieStorage extends SimpleStorage {
  setItem(key: string, value: string, expiresAt?: string | null): void; // we need to set expiration date for non-session cookies
  getItem(key?: string): string | null; // returns all cookies if no key is provided
  removeItem(key: string): void;
}

/**
 * Cookie storage provides a method to store data in a browser cookie.
 * All values are stored as base64 encoded strings.
 */
export class CookieStorage implements ICookieStorage {
  private readonly cookieOptions: CookieOptions;
  constructor(cookieOptions: CookieOptions) {
    this.cookieOptions = cookieOptions;
  }

  /**
   * Cookies have size limitations, so we have to split incoming values into separate cookies.
   * value should be a JSON parsable object. Each key in the object will be stored as a separate cookie.
   *
   * @param key
   * @param value
   */
  setItem(key: string, value: string): void {
    const existingCookie = JSON.parse(this.getItem(key) || '{}');
    const parsedValue = JSON.parse(value);
    const expiresAt = this.cookieOptions.sessionCookie ? null : this.cookieOptions.expires?.toUTCString() ?? null;

    // Create a separate cookie for each key in the value object
    const keysToKeep = new Set(
      Object.keys(parsedValue).map((k) => {
        const cookieKey = `${key}_${k}`;
        const cookieValue = btoa(JSON.stringify(parsedValue[k]));
        Cookie.set(cookieKey, cookieValue, expiresAt, this.cookieOptions);
        return k;
      }),
    );

    // Remove any existing cookies that are not in the new value object
    Object.keys(existingCookie)
      .filter((k) => !keysToKeep.has(k))
      .forEach((k) => {
        Cookie.delete(`${key}_${k}`);
      });
  }

  getItem(key: string): string | null {
    const existingCookies = Cookie.get();
    const cookieEntries = Object.keys(existingCookies)
      .filter((k) => k.startsWith(key))
      .map((k) => [k.replace(`${key}_`, ''), JSON.parse(atob(existingCookies[k]))]);

    return cookieEntries.length === 0 ? null : JSON.stringify(Object.fromEntries(cookieEntries));
  }

  removeItem(key: string): void {
    const existingCookie = JSON.parse(this.getItem(key) || '{}');
    Object.keys(existingCookie).forEach((k) => {
      Cookie.delete(`${key}_${k}`);
    });
  }
}

class Cookie {
  static get(): Record<string, string>;
  static get(name: string): string | undefined;
  static get(name?: string): Record<string, string> | string | undefined {
    if (name) {
      return JsCookie.get(name);
    }
    return JsCookie.get();
  }

  static set(name: string, value: string, expiresAt: string | null, options: CookieOptions): string {
    const { sameSite, secure } = options;
    if (typeof secure === 'undefined' || typeof sameSite === 'undefined') {
      throw new AuthSdkError('CookieStorage: "secure" and "sameSite" cookie options must be provided');
    }
    const cookieOptions: CookieOptions = {
      path: options.path || '/',
      secure,
      sameSite,
    };

    if (expiresAt && Boolean(Date.parse(expiresAt))) {
      // if no expiresAt is provided, the cookie will be a session cookie
      cookieOptions.expires = new Date(expiresAt);
    }

    JsCookie.set(name, value, cookieOptions);

    const cookie = this.get(name);
    if (!cookie) {
      throw new AuthSdkError(`CookieStorage: Unable to set cookie ${name}`);
    }
    return cookie;
  }

  static delete(name: string): void {
    JsCookie.remove(name, { path: '/' });
  }
}
